import React from 'react';

import {
  Field,
  FieldInputProps,
  FieldProps,
  FieldRenderProps,
} from 'react-final-form';

import { MultipleSuggestedInput as Input } from '@vk-hr-tek/ui/input';

import { compose } from '../validation';

import { MultipleSuggestedInputProps } from './MultipleSuggestedInput.types';

/**
 * Функция-обработчик изменения значения ввода.
 * @param {FieldInputProps} input - Объект с пропсами ввода из react-final-form.
 * @param {Function} [onChange] - Дополнительная функция-обработчик изменения значения.
 * @returns {Function} Функция, которая вызывает onChange из react-final-form и дополнительную onChange, если она передана.
 */
const handleChange =
  (
    input: FieldInputProps<
      string | string[] | number | number[] | undefined,
      HTMLElement
    >,
    onChange?: (value: string[] | undefined) => void,
  ) =>
  (value: string[] | undefined) => {
    input.onChange(value);

    if (onChange) {
      onChange(value);
    }
  };

/**
 * Компонент ввода с подсказками с множественным выбором.
 * @param {MultipleSuggestedInputProps & FieldProps} props - Пропсы компонента, объединяющие пропсы MultipleSuggestedInput и FieldProps из react-final-form.
 * @returns {JSX.Element} Компонент Field, который рендерит кастомный компонент ввода Input.
 */
export const MultipleSuggestedInput = (
  props: MultipleSuggestedInputProps &
    FieldProps<string[] | undefined, FieldRenderProps<string[] | undefined>>,
) => {
  const {
    type,
    name,
    label,
    onChange,
    placeholder,
    disabled = false,
    required = false,
    clearable = false,
    loading = false,
    tooltip,
    helperText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    minInputValueLengthForRequest = minInputValueLength,
    validate,
    validationWithoutTouch = false,
    validationWhenDisabled = false,
    ...rest
  } = props;

  return (
    <Field
      name={name}
      {...rest}
      validate={Array.isArray(validate) ? compose(validate) : validate}
    >
      {({ input, meta }) => {
        /**
         * Флаг для отображения ошибки в зависимости от состояния disabled
         * @type {boolean}
         */
        const errorDisabled = disabled || !validationWhenDisabled;

        /**
         * Флаг для отображения ошибки
         * @type {boolean}
         */
        const renderError =
          !errorDisabled && validationWithoutTouch
            ? !!input.value && !!meta.error
            : meta.touched && !!meta.error;

        const baseInputProps = {
          ...input,
          label,
          onChange: handleChange(input, onChange),
          placeholder,
          disabled,
          required,
          clearable,
          loading,
          tooltip,
          error: renderError,
          helperText: (renderError && meta.error) || helperText,
          renderOption,
          limit,
          minInputValueLength,
          minInputValueLengthForRequest,
        };

        return props.type === 'load' ? (
          <Input
            {...baseInputProps}
            type="load"
            loadItems={props.loadItems}
            mask={props.mask}
          />
        ) : (
          <Input
            {...baseInputProps}
            type="local"
            externalItems={props.externalItems}
          />
        );
      }}
    </Field>
  );
};
